import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  useParams,
  useLocation,
  Navigate
} from "react-router-dom";
import React, { useState, useEffect } from "react";

import Navbar from "./Sections/Navbar";
import Hero from "./Sections/Hero";
import About from "./Sections/About";
import WhyChooseUs from "./Sections/WhyChooseUs";
import Services from "./Sections/Services";
import Team from "./Sections/Team";
import Blog from "./Sections/Blog";
import Footer from "./Sections/Footer";
import Contact from "./Sections/Contact";
import Testimonial from "./Sections/Testimonial";
import Foundation from "./Sections/Foundation";

import BlogPage from "./components/BlogPage";
import ServicePage from "./components/ServicePage";
import SERVICE_DATA from "./data/services";
import BLOG_DATA_EN from "./data/blogs"; // Renamed for clarity
import BLOG_DATA_FR from "./data/blogs.fr"; // Import French blog data
import { whoWeServeData as whoWeServeFrData } from "./data/whoWeServe.fr";
import * as whoWeServeEnModule from "./data/whoWeServe";
import WhoWeServePage from "./components/WhoWeServePage";

import Careers from "./Sections/Careers/Careers.jsx";
// import Signup from "./components/Signup/Signup";
import Panel from "./components/Panel/Panel";
import Login from "./components/Login/Login";
import Application from "./components/Panel/Application";
import Employees from "./components/Panel/Employees";
import Settings from "./components/Panel/Settings";
import FooterPanel from "./components/SectionsPanel/FooterPanel";
import NavbarPanel from "./components/SectionsPanel/NavbarPanel";
import useAuthWatcher from "./hooks/useAuthWatcher";
import CookieConsent from "./components/CookieConsent";
import PrivacyPolicy from "./components/PrivacyPolicy";
import PrivacyPolicyFR from "./components/PrivacyPolicy.fr";
import TermsOfService from "./components/TermsOfService";
import TermsOfServiceFR from "./components/TermsOfService.fr";

/* --- helpers --- */
function ScrollToTop() {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    // Instant scroll to top (no smooth behavior)
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    
    // Add loading class to prevent flash
    document.body.classList.add('page-loading');
    
    const timer = setTimeout(() => {
      document.body.classList.remove('page-loading');
    }, 300);
    
    return () => clearTimeout(timer);
  }, [pathname]);
  
  return null;
}

/* --- pages --- */
function Home() {
  return (
    <>
      <Hero />
      <About />
      <Services />
      <WhyChooseUs />
      <Team />
      <Testimonial />
      <Blog />
    </>
  );
}

function BlogRoute() {
  const { slug } = useParams();
  const location = useLocation();

  // Detect language from URL and select the correct data
  const isFr = location.pathname.startsWith("/fr");
  const blogData = isFr ? BLOG_DATA_FR : BLOG_DATA_EN;

  const data = blogData[slug];

  return data ? (
    <BlogPage data={data} />
  ) : (
    <div style={{ padding: 24 }}>Blog post not found.</div>
  );
}

function WhoWeServeRoute() {
  const { slug } = useParams();
  const location = useLocation();
  // detect language from URL prefix (Navbar uses same rule)
  const isFr = (location.pathname || "").startsWith("/fr");
  // support either named export `whoWeServeData` or default export from the english module
  const whoEn = whoWeServeEnModule.whoWeServeData || whoWeServeEnModule.default || {};
  const dataset = isFr ? (whoWeServeFrData || whoEn) : whoEn;

  // try direct lookup, then try decoding the slug (safe fallback)
  const data = dataset[slug] || dataset[decodeURIComponent(slug || "")];

  return data ? <WhoWeServePage data={data} /> : <div>Page not found</div>;
}

function ServiceRoute() {
  const { slug } = useParams();
  const data = SERVICE_DATA[slug];
  return data ? (
    <ServicePage data={data} />
  ) : (
    <div style={{ padding: 24 }}>Service not found.</div>
  );
}

/* Layout keeps Navbar/Footer on every route */
function Layout() {
  return (
    <>
      <Navbar />
      <Outlet />
      <Footer />
    </>
  );
}

function PrivateRoute({ children }) {
  const isLoggedIn = Boolean(sessionStorage.getItem("token"));
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

function AuthWatcher() {
  useAuthWatcher();
  return null; // no UI
}

function LayoutPanel() {
  return (
    <>
      <NavbarPanel />
      <Outlet />   {/* This will render your Panel with Boxes */}
      {/* <FooterPanel /> */}

      <FooterPanel /> 
    </>
  );
}

/* --- router --- */
export default function App() {
  const protectedRoutes = ["/panel", "/Application", "/Employees", "/Settings"];
  
  // Initialize language from localStorage, default to 'en'
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    return savedLanguage || 'en';
  });

  // Save language preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('preferredLanguage', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prevLang) => (prevLang === "en" ? "fr" : "en"));
  };
  
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthWatcher protectedRoutes={protectedRoutes} />

      <Routes>
        {/* ===== English (default) ===== */}
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/services/:slug" element={<ServiceRoute />} />
          <Route path="/blog/foundation" element={<Foundation />} />
          <Route path="/blog/:slug" element={<BlogRoute />} />
          <Route path="/who-we-serve/:slug" element={<WhoWeServeRoute />} />

          {/* <Route path="/signup" element={<Signup />} /> */}
          <Route path="/login" element={<Login />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* 404 fallback (EN) */}
          <Route path="*" element={<div style={{ padding: 24 }}>Page not found.</div>} />
        </Route>

        {/* ===== French mirror under /fr/* =====
            Same components; i18n provides FR strings via URL prefix */}
        <Route path="/fr" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="contact" element={<Contact />} />
          <Route path="services/:slug" element={<ServiceRoute />} />
          <Route path="blog/foundation" element={<Foundation />} />
          <Route path="blog/:slug" element={<BlogRoute />} />
          <Route path="who-we-serve/:slug" element={<WhoWeServeRoute />} />

          {/* If you also want FR versions of these, uncomment: */}
          {/* <Route path="signup" element={<Signup />} />
              <Route path="login" element={<Login />} /> */}
          <Route path="careers" element={<Careers />} />
          <Route path="privacy" element={<PrivacyPolicyFR />} />
          <Route path="terms" element={<TermsOfServiceFR />} />

          {/* 404 fallback (FR): send to /fr home */}
          <Route path="*" element={<Navigate to="/fr" replace />} />
        </Route>

        {/* ===== Panel / protected area (kept EN-only) ===== */}
        <Route element={<LayoutPanel />}>
          <Route
            path="/panel"
            element={
              <PrivateRoute>
                <Panel />
              </PrivateRoute>
            }
          />
          {/* Note: your paths use capital letters; keep consistent */}
          <Route
            path="/Application"
            element={
              <PrivateRoute>
                <Application />
              </PrivateRoute>
            }
          />
          <Route
            path="/Employees"
            element={
              <PrivateRoute>
                <Employees />
              </PrivateRoute>
            }
          />

           <Route
            path="/Settings"
            element={
              <PrivateRoute>
                <Settings/>
                
              </PrivateRoute>
            }
          />
          <Route path="*" element={<div style={{ padding: 24 }}>Page not found.</div>} />
        </Route>
      </Routes>
      <CookieConsent />
    </BrowserRouter>
  );
}
